import React, { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useLocation,
} from 'react-router-dom';

import Dialog from '@liquidweb-dev/undercurrent/Dialog';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogActions from '@liquidweb-dev/undercurrent/DialogActions';
import Button from '@liquidweb-dev/undercurrent/Button';
import toast from '@liquidweb-dev/undercurrent/toast';
import SuspenseFallback from 'components/routers/RouteHandler/SuspenseFallback';
import SupportLayout from 'components/SupportLayout';
import { username as usernameSelector } from 'modules/auth/authSelectors';
import useSupportRequestAuthenticate from 'modules/queries/support/request/useAuthenticate';
import { roles } from 'utility/constants/roles';
import RoleRestrictedRoute from 'utility/routing/RoleRestrictedRoute';

const MyCases = lazy(() => import('pages/support/MyCases'));
const FeaturedHelpDocs = lazy(() => import('pages/support'));

const AuthenticateSupportRequest = () => {
	const location = useLocation();
	const history = useHistory();
	const loggedInUser = useSelector(usernameSelector);

	const searchParams = new URLSearchParams(location.search);
	const caseId = searchParams.get('case_id') || searchParams.get('id');

	const { mutate: authenticate, isLoading } = useSupportRequestAuthenticate();

	const handleClose = () => history.push('/');

	const handleConfirm = () => {
		if (!caseId) {
			toast.danger('It looks like your link is missing a Case ID');
			return;
		}

		authenticate(
			{ id: caseId, username: loggedInUser },
			{
				onSuccess: () => {
					handleClose();
				},
				onError: () => {
					toast.danger('There was an issue with your link');
				},
			},
		);
	};

	return (
		<Dialog open onClose={handleClose}>
			<DialogTitle onClose={handleClose}>Confirmation Needed</DialogTitle>
			<DialogContent>
				You are signed in as {loggedInUser}. Please confirm you would like to
				authenticate this support request with this account.
			</DialogContent>
			<DialogActions>
				<Button variant="secondary" onClick={handleClose}>
					Cancel
				</Button>
				<Button onClick={handleConfirm} loading={isLoading}>
					Confirm
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const SupportRouter = () => {
	const { pathname } = useLocation();
	return (
		<Switch>
			{/* legacy routes */}
			<Redirect from="/support/status" to="/support" />
			<Redirect from="/support/mytickets" to="/support/mycases" />
			<Route>
				<SupportLayout tabsValue={pathname}>
					<Suspense fallback={<SuspenseFallback />}>
						<Switch>
							<RoleRestrictedRoute
								exact
								whitelistedRoles={[
									roles.PURCHASER,
									roles.TECHNICIAN,
									roles.SUSPENDED_OWNER,
								]}
								path="/support/mycases"
								component={MyCases}
							/>
							<RoleRestrictedRoute
								exact
								whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
								path="/support/request/authenticate"
								component={AuthenticateSupportRequest}
							/>
							<RoleRestrictedRoute
								exact
								whitelistedRoles={[
									roles.PURCHASER,
									roles.TECHNICIAN,
									roles.SUSPENDED_OWNER,
								]}
								path="/support"
								component={FeaturedHelpDocs}
							/>
						</Switch>
					</Suspense>
				</SupportLayout>
			</Route>
		</Switch>
	);
};

export default SupportRouter;
